import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Leaf, Eye, EyeOff, ArrowLeft, ArrowRight, UserPlus, CheckCircle, Lock, ShieldCheck, FileText, Mail, CreditCard, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contract content in English for international practitioners
const CONTRACT_CONTENT = `
PRACTITIONER ACCESS AGREEMENT FOR THE APORA.NET WEB PORTAL
Independent Practitioner

Reference: APORA-PORTAL

PREAMBLE
The Publisher operates a digital solution enabling independent practitioners to present their business, organize and receive appointments, and, depending on configuration, collect online payments and conduct video conferencing sessions. The Practitioner wishes to access these services in order to develop and manage their business.

Important: This document constitutes a service provision agreement (provision of a platform). It does not constitute an employment contract. The Practitioner remains fully independent.

1. PARTIES
The Publisher / Platform: APORA, SAS, with a capital of €10,000, registered under number 91212461700032, with its registered office located in Plaisir, France, represented by Natalie Guimelli, hereinafter referred to as "the Publisher."

The Practitioner: Any natural or legal person wishing to access the Platform's services, hereinafter referred to as "the Practitioner."

2. DEFINITIONS
• Platform: web portal made available by the Publisher, enabling online presence, profile management, and appointment management.
• Account: Practitioner's personal space on the Platform.
• User / Client: person making an appointment with the Practitioner via the Platform.
• Services: features offered (appointment booking, email messaging, online payment, video conferencing, etc.).
• Transaction: payment made online via the Platform and/or payment gateway.

3. PURPOSE
The purpose of this agreement is to define the conditions under which the Publisher provides the Practitioner access to the Platform so that the Practitioner may:
• create and manage a professional profile and publish information about their services;
• receive contact requests and/or appointments (depending on activated features);
• manage their schedule, visibility, and contribute to developing their clientele in compliance with applicable rules;
• offer, if activated, online payment and/or video conferencing, according to the terms of this Agreement.

4. INDEPENDENCE OF THE PARTIES
The Practitioner carries out their activity in complete independence. The Agreement does not imply any exclusivity, subordination, or employment relationship. The Practitioner remains solely responsible for organizing their activity, their professional choices, their regulatory compliance, as well as their taxation and accounting.

5. PLATFORM ACCESS
After electronic acceptance of the Agreement by the Practitioner (validation on the website), the Publisher provides the Practitioner with:
• an access code allowing activation of the Account;
• a procedure for creating an account and configuring settings.

The Practitioner agrees to maintain the confidentiality of their credentials and to notify the Publisher immediately in case of unauthorized use.

6. FEES AND BILLING
6.1 Initial Registration Fee: €50 (includes registration, verification, and administrative fees - charged after profile validation)
6.2 Monthly Subscription: €50/month (charged at the end of each month)

7. PRACTITIONER OBLIGATIONS
The Practitioner agrees to:
• respect the ethical rules applicable to their profession
• maintain professional secrecy
• hold the required authorizations, diplomas, and insurance
• manage their accounting and tax obligations

8. DURATION AND TERMINATION
The Agreement takes effect on the date of electronic acceptance. It is concluded for an indefinite period.
• Termination by the Practitioner: possible at any time with 30 days' notice
• Termination by the Publisher: possible in case of serious breach

9. DATA PROTECTION (GDPR COMPLIANCE)
The Platform complies with the General Data Protection Regulation (GDPR). Personal data is processed securely and in accordance with European regulations. All payments are processed through Stripe, a PCI-DSS Level 1 certified payment provider.

10. APPLICABLE LAW
The Agreement is governed by French law.

APORA — Practitioner Access Agreement for APORA.NET Web Portal
Ref. APORA-PORTAL-PI • 31/03/2026
`;

const PractitionerSignup = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, isPractitioner } = useAuth();
  const { t } = useLanguage();
  
  // Step management
  const [step, setStep] = useState(1);
  
  // Step 1: Request form
  const [requestForm, setRequestForm] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [requestSent, setRequestSent] = useState(false);
  
  // Step 2: Code verification
  const [accessCode, setAccessCode] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  
  // Step 3: Contract
  const [contractAccepted, setContractAccepted] = useState(false);
  
  // Step 4: Payment done
  const [paymentDone, setPaymentDone] = useState(false);
  
  // Step 5: Account creation
  const [form, setForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'practitioner'
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      if (isPractitioner) {
        navigate('/practitioner/dashboard');
      } else {
        navigate('/client/dashboard');
      }
    }
  }, [isAuthenticated, isPractitioner, navigate]);

  // Check URL for payment success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setStep(5);
      setPaymentDone(true);
      setContractAccepted(true);
      setCodeVerified(true);
      const storedData = sessionStorage.getItem('practitioner_signup_data');
      if (storedData) {
        const data = JSON.parse(storedData);
        setRequestForm(data);
        setForm(prev => ({
          ...prev,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name
        }));
      }
      toast.success(t('practitionerSignup.paymentSuccess'));
    }
  }, [t]);

  // Step 1: Send request
  const handleSendRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/practitioner-request`, requestForm);
      setRequestSent(true);
      toast.success(t('practitionerSignup.requestSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-practitioner-access`, { 
        code: accessCode,
        email: requestForm.email 
      });
      setCodeVerified(true);
      setStep(3);
      toast.success(t('practitionerSignup.codeVerified'));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Accept contract and proceed to payment
  const handleAcceptContract = async () => {
    if (!contractAccepted) {
      toast.error(t('practitionerSignup.mustAcceptTerms'));
      return;
    }
    
    sessionStorage.setItem('practitioner_signup_data', JSON.stringify(requestForm));
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/practitioner-registration`, {
        email: requestForm.email,
        first_name: requestForm.first_name,
        last_name: requestForm.last_name,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment error");
      setLoading(false);
    }
  };

  // Step 5: Create account
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (form.password.length < 16) {
      toast.error(t('practitionerSignup.passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      await register(form);
      sessionStorage.removeItem('practitioner_signup_data');
      toast.success(t('practitionerSignup.accountCreated'));
      navigate('/practitioner/setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration error");
    } finally {
      setLoading(false);
    }
  };

  // Download contract
  const downloadContract = () => {
    const blob = new Blob([CONTRACT_CONTENT], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Contrat_Praticien_Apora.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-50 to-sage-50 flex flex-col" data-testid="practitioner-signup-page">
      {/* Header */}
      <div className="container-app py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-700">
          <ArrowLeft className="w-4 h-4" />
          {t('practitionerSignup.backToHome')}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-sage-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Leaf className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-semibold text-foreground">
              {t('practitionerSignup.title')}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t('practitionerSignup.subtitle')}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${step >= s ? 'bg-sage-500 text-white' : 'bg-sand-200 text-muted-foreground'}`}>
                    {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                  </div>
                  {s < 5 && <div className={`w-8 h-0.5 ${step > s ? 'bg-sage-500' : 'bg-sand-200'}`} />}
                </div>
              ))}
            </div>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-soft p-8 border border-sand-200">
            
            {/* Step 1: Request */}
            {step === 1 && !requestSent && (
              <form onSubmit={handleSendRequest} className="space-y-6">
                <div className="text-center mb-6">
                  <Mail className="w-12 h-12 text-sage-500 mx-auto mb-3" />
                  <h2 className="text-xl font-semibold">{t('practitionerSignup.step1Title')}</h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    {t('practitionerSignup.step1Desc')}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('practitionerSignup.firstName')} *</Label>
                    <Input
                      value={requestForm.first_name}
                      onChange={(e) => setRequestForm(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('practitionerSignup.lastName')} *</Label>
                    <Input
                      value={requestForm.last_name}
                      onChange={(e) => setRequestForm(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('practitionerSignup.email')} *</Label>
                  <Input
                    type="email"
                    value={requestForm.email}
                    onChange={(e) => setRequestForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="rounded-xl"
                  />
                </div>

                <Button type="submit" className="w-full btn-primary" disabled={loading}>
                  {loading ? t('practitionerSignup.sending') : t('practitionerSignup.sendRequest')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            )}

            {/* Step 1b: Request sent, show code input */}
            {step === 1 && requestSent && (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div className="text-center mb-6">
                  <Lock className="w-12 h-12 text-sage-500 mx-auto mb-3" />
                  <h2 className="text-xl font-semibold">{t('practitionerSignup.step2Title')}</h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    {t('practitionerSignup.step2Desc')} <strong>{requestForm.email}</strong>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{t('practitionerSignup.accessCode')} *</Label>
                  <Input
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder={t('practitionerSignup.accessCodePlaceholder')}
                    required
                    className="rounded-xl text-center text-lg tracking-wider"
                  />
                </div>

                <Button type="submit" className="w-full btn-primary" disabled={loading}>
                  {loading ? t('practitionerSignup.verifying') : t('practitionerSignup.verifyCode')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  {t('practitionerSignup.noCodeReceived')}{' '}
                  <button 
                    type="button"
                    onClick={() => setRequestSent(false)}
                    className="text-sage-600 hover:underline"
                  >
                    {t('practitionerSignup.resendRequest')}
                  </button>
                </p>
              </form>
            )}

            {/* Step 3: Contract */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <FileText className="w-12 h-12 text-sage-500 mx-auto mb-3" />
                  <h2 className="text-xl font-semibold">{t('practitionerSignup.step3Title')}</h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    {t('practitionerSignup.step3Desc')}
                  </p>
                </div>

                {/* Contract display */}
                <div className="bg-sand-50 rounded-xl p-4 max-h-96 overflow-y-auto border border-sand-200">
                  <pre className="whitespace-pre-wrap text-sm font-sans text-foreground">
                    {CONTRACT_CONTENT}
                  </pre>
                </div>

                {/* Download button */}
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={downloadContract}
                  className="w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('practitionerSignup.downloadContract')}
                </Button>

                {/* Accept checkbox */}
                <div className="flex items-start gap-3 p-4 bg-sage-50 rounded-xl">
                  <Checkbox
                    id="accept-contract"
                    checked={contractAccepted}
                    onCheckedChange={setContractAccepted}
                    className="mt-1"
                  />
                  <label htmlFor="accept-contract" className="text-sm cursor-pointer">
                    {t('practitionerSignup.acceptTerms')} <strong>3€</strong> {t('practitionerSignup.willBeCharged')}
                  </label>
                </div>

                <Button 
                  onClick={handleAcceptContract} 
                  className="w-full btn-primary" 
                  disabled={!contractAccepted || loading}
                >
                  {loading ? t('practitionerSignup.redirectingPayment') : `${t('practitionerSignup.acceptAndPay')} 3€`}
                  <CreditCard className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 5: Create account */}
            {step === 5 && (
              <form onSubmit={handleRegister} className="space-y-6">
                <div className="text-center mb-6">
                  <UserPlus className="w-12 h-12 text-sage-500 mx-auto mb-3" />
                  <h2 className="text-xl font-semibold">{t('practitionerSignup.step5Title')}</h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    {t('practitionerSignup.step5Desc')}
                  </p>
                </div>

                <div className="p-4 bg-green-50 rounded-xl border border-green-200 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 text-sm">{t('practitionerSignup.paymentReceived')}</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('practitionerSignup.firstName')}</Label>
                    <Input
                      value={form.first_name}
                      onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('practitionerSignup.lastName')}</Label>
                    <Input
                      value={form.last_name}
                      onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('practitionerSignup.email')}</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('practitionerSignup.phone')}</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('practitionerSignup.password')} (min. 16 {t('practitionerSignup.passwordMin')})</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                      minLength={16}
                      className="rounded-xl pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {form.password.length}/16 {t('practitionerSignup.passwordMin')}
                  </p>
                </div>

                <Button type="submit" className="w-full btn-primary" disabled={loading}>
                  {loading ? t('practitionerSignup.creating') : t('practitionerSignup.createAccount')}
                  <ShieldCheck className="w-4 h-4 ml-2" />
                </Button>
              </form>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('practitionerSignup.alreadyAccount')}{' '}
            <Link to="/auth" className="text-sage-600 hover:underline font-medium">
              {t('practitionerSignup.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PractitionerSignup;
